/**
 * MeetingPrepTab - Displays prep briefs for meetings.
 */

import { useQuery } from '@tanstack/react-query';
import { AlertCircle, FileSearch02, RefreshCw01 } from '@untitledui/icons';
import React from 'react';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import {
  createEntityMarkdownComponents,
  entitySanitizeSchema,
  preprocessEntityTags,
} from '@/lib/markdown/entityRendering';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import { getPropertyValue } from '@/types/crm';

import { createCRMObjectClient } from '../../core/CRMObjectClient';

interface MeetingPrepTabProps {
  /** Organization ID */
  orgId: number;
  /** Entity ID of the meeting */
  meetingEntityId: string;
  /** Meeting title for deriving prep brief title */
  meetingTitle?: string;
}

/**
 * Loading skeleton for meeting prep.
 */
function LoadingSkeleton() {
  return (
    <div
      className='p-4 rounded-xl animate-pulse'
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-primary)',
      }}
    >
      <div
        className='h-5 rounded w-2/3 mb-3'
        style={{ backgroundColor: 'var(--color-bg-secondary)' }}
      />
      <div
        className='h-4 rounded w-1/4 mb-4'
        style={{ backgroundColor: 'var(--color-bg-secondary)' }}
      />
      <div
        className='h-4 rounded w-3/4 mb-2'
        style={{ backgroundColor: 'var(--color-bg-secondary)' }}
      />
      <div
        className='h-4 rounded w-2/3'
        style={{ backgroundColor: 'var(--color-bg-secondary)' }}
      />
    </div>
  );
}

/**
 * Error state component.
 */
function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className='flex flex-col items-center justify-center py-12 text-center'>
      <p
        className='text-sm mb-4'
        style={{ color: 'var(--color-text-error-primary)' }}
      >
        We couldn&apos;t load the prep brief
      </p>
      <Button
        color='secondary'
        size='sm'
        onClick={onRetry}
        iconLeading={RefreshCw01}
      >
        Retry
      </Button>
    </div>
  );
}

/**
 * Get badge color for prep status.
 */
function getStatusBadgeColor(
  status: string
): 'warning' | 'success' | 'error' | 'gray' {
  switch (status) {
    case 'in_progress':
      return 'warning';
    case 'complete':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'gray';
  }
}

/**
 * Meeting Prep tab component.
 */
export function MeetingPrepTab({
  orgId,
  meetingEntityId,
  meetingTitle,
}: MeetingPrepTabProps) {
  // Fetch meeting prep objects associated with this meeting
  const {
    data: prepBriefs,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['meetingPrep', orgId, meetingEntityId],
    queryFn: async () => {
      const client = createCRMObjectClient(orgId, 'ahoy_research');

      // Query for research objects associated with this meeting
      const response = await client.list({
        associated_with_entity_id: meetingEntityId,
        properties: [
          'ahoy_summary',
          'ahoy_status',
          'ahoy_created_at',
          'ahoy_research_type',
        ],
        order_by: '-ahoy_created_at',
      });

      return response.results;
    },
    enabled: !!meetingEntityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return <ErrorState onRetry={refetch} />;
  }

  // Empty state
  if (!prepBriefs || prepBriefs.length === 0) {
    return (
      <EmptyState
        variant='empty'
        title='No prep brief available'
        description='Prep briefs will appear here when generated for this meeting.'
        icon={
          <FeaturedIcon
            icon={FileSearch02}
            size='md'
            theme='light-outline'
            color='gray'
          />
        }
        size='sm'
      />
    );
  }

  // Display prep brief (typically only one per meeting)
  const prepBrief = prepBriefs[0];
  // Title derived from parent meeting's title (passed as meetingTitle prop)
  const title = meetingTitle ? `Prep: ${meetingTitle}` : 'Meeting prep brief';
  const summary = getPropertyValue<string>(prepBrief.properties?.ahoy_summary);
  const status =
    getPropertyValue<string>(prepBrief.properties?.ahoy_status) ||
    'in_progress';
  const createdAt = getPropertyValue<string>(
    prepBrief.properties?.ahoy_created_at
  );

  return (
    <div
      className='p-6 rounded-xl'
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-primary)',
      }}
    >
      {/* Header */}
      <div className='flex items-start justify-between mb-4'>
        <div className='flex-1'>
          <h3
            className='text-lg font-semibold mb-1'
            style={{ color: 'var(--color-text-primary)' }}
          >
            {title}
          </h3>
          <div className='flex items-center gap-3'>
            <Badge color={getStatusBadgeColor(status)} size='sm'>
              {status
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')}
            </Badge>
            <span
              className='text-sm'
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              Generated {formatRelativeTime(createdAt || '')}
            </span>
          </div>
        </div>
      </div>

      {/* Status-specific content */}
      {status === 'in_progress' && (
        <div
          className='flex items-center gap-3 p-4 rounded-lg'
          style={{ backgroundColor: 'var(--color-bg-primary)' }}
        >
          <LoadingIndicator size='xs' inline />
          <p
            className='text-sm'
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Prep brief is being generated…
          </p>
        </div>
      )}

      {status === 'failed' && (
        <div
          className='flex items-start gap-3 p-4 rounded-lg'
          style={{ backgroundColor: 'var(--color-bg-primary)' }}
        >
          <AlertCircle
            className='size-5 shrink-0 mt-0.5'
            style={{ color: 'var(--color-text-error-primary)' }}
          />
          <p
            className='text-sm'
            style={{ color: 'var(--color-text-error-primary)' }}
          >
            We couldn&apos;t generate the prep brief. Try again.
          </p>
        </div>
      )}

      {status === 'complete' && summary && (
        <MarkdownContent
          content={preprocessEntityTags(summary)}
          rehypePlugins={[rehypeRaw, [rehypeSanitize, entitySanitizeSchema]]}
          components={createEntityMarkdownComponents(orgId)}
          className='prose prose-sm max-w-none dark:prose-invert'
        />
      )}

      {status === 'complete' && !summary && (
        <p className='text-sm' style={{ color: 'var(--color-text-tertiary)' }}>
          No summary available
        </p>
      )}
    </div>
  );
}
